import { useForm, Link } from '@inertiajs/react'
import { Layout } from '@/components/Layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ArrowLeft } from 'lucide-react'

interface Props {
  client: {
    name: string
    code: string
    notes: string
  }
  errors: Record<string, string[]>
}

export default function ClientNew({ client, errors }: Props) {
  const { data, setData, post, processing } = useForm({
    client: {
      name: client.name,
      code: client.code,
      notes: client.notes,
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/clients')
  }

  const formData = data.client
  const setFormData = (key: keyof typeof formData, value: string) => {
    setData('client', { ...formData, [key]: value })
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link href="/clients">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back</span>
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">New Client</h1>
            <p className="text-muted-foreground">
              Add a new client organization.
            </p>
          </div>
        </div>

        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Client Details</CardTitle>
            <CardDescription>
              Enter the details for the new client.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData('name', e.target.value)}
                  placeholder="Acme Corporation"
                  autoFocus
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">Code</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData('code', e.target.value)}
                  placeholder="acme"
                />
                <p className="text-sm text-muted-foreground">
                  A unique identifier for this client (lowercase, no spaces).
                </p>
                {errors.code && (
                  <p className="text-sm text-destructive">{errors.code[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData('notes', e.target.value)}
                  placeholder="Optional notes about this client..."
                  rows={4}
                />
                {errors.notes && (
                  <p className="text-sm text-destructive">{errors.notes[0]}</p>
                )}
              </div>

              <div className="flex gap-4">
                <Button type="submit" disabled={processing}>
                  {processing ? 'Creating...' : 'Create Client'}
                </Button>
                <Button variant="outline" asChild>
                  <Link href="/clients">Cancel</Link>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}
